import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, calcDocument, CURRENCIES, D, DOCUMENT_TYPE_META, type DocumentType, money, parseMoney, PAYMENT_METHODS, SCALE, toScale } from '@makila/fiscal';
import clsx from 'clsx';
import { BookOpen, ChevronDown, Plus, Send, SlidersHorizontal, Trash2, Truck, UserPlus } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { CatalogModal, CustomerPicker, type PickCustomer, type PickProduct, ProductSuggest, useReference } from '../components/pickers';
import {
  Alert, Badge, Button, Card, Field, Input, Loading, Modal, PageHeader, Select, SuggestInput, Switch, Table, Td, Textarea, Th, errorText, useConfirm, useToast,
} from '../components/ui';
import { ApiError, get, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, mt, today } from '../lib/format';
import { PAYMENT_TERMS, UNITS } from '../lib/lists';
import { CustomerForm } from './customers';

interface Line {
  key: string; productId: string | null; productCode: string | null; description: string; note: string;
  quantity: string; unit: string; unitPrice: string; priceIncludesTax: boolean; discountPct: string; taxRate: number;
  exemptionCode: string; exemptionReason: string; open?: boolean;
}
const emptyLine = (discount = ''): Line => ({ key: crypto.randomUUID(), productId: null, productCode: null, description: '', note: '', quantity: '1', unit: 'un', unitPrice: '', priceIncludesTax: false, discountPct: discount, taxRate: 16, exemptionCode: '', exemptionReason: '' });
const num = (s: string) => parseMoney(s) ?? '0';
/** Como a API: arredondado à escala gravada antes de calcular, para a pré-visualização ser o que se emite. */
const at = (s: string, places: number) => toScale(num(s), places);
const canon = (s: string | number | null | undefined) => (s === null || s === undefined || s === '' ? '' : String(Number(s)));
type Terms = '' | 'custom' | `${number}`;

/** Linha do resumo: rótulo à esquerda, valor tabular à direita. */
function Row({ label, value, strong, muted, neg }: { label: ReactNode; value: string; strong?: boolean; muted?: boolean; neg?: boolean }) {
  return (
    <div className={clsx('flex items-baseline justify-between gap-4 py-1', muted && 'text-xs text-[var(--muted)]', strong && 'font-semibold')}>
      <dt className={muted ? '' : 'text-[var(--text-2)]'}>{label}</dt><dd className="tabular-nums">{neg && Number(value) ? '−' : ''}{mt(value)}</dd>
    </div>
  );
}

/** O essencial da ficha do cliente, para confirmar antes de emitir. */
function CustomerFacts({ c, balance, lastPurchase }: {
  c: PickCustomer & { phone?: string | null; credit_limit?: string | null }; balance?: string; lastPurchase?: string | null;
}) {
  if (c.is_final_consumer) return <p className="mt-3 text-sm text-[var(--muted)]">Venda a consumidor final — o documento sai sem NUIT do adquirente.</p>;
  const owes = balance !== undefined ? Number(balance) : 0;
  const over = c.credit_limit && Number(c.credit_limit) > 0 && owes > Number(c.credit_limit);
  const facts: [string, ReactNode][] = [
    ['Morada', [c.address, c.city].filter(Boolean).join(', ') || <span className="text-amber-600">Sem morada na ficha</span>],
    ['Email', c.billing_email ?? c.email ?? <span className="text-[var(--muted)]">—</span>],
    ['Telefone', c.phone || <span className="text-[var(--muted)]">—</span>],
    ['Condições', [c.payment_terms_days === null ? null : c.payment_terms_days === 0 ? 'Pronto pagamento' : `${c.payment_terms_days} dias`,
      c.default_payment_method ? PAYMENT_METHODS[c.default_payment_method as keyof typeof PAYMENT_METHODS] : null,
      Number(c.default_discount_pct) ? `desc. ${canon(c.default_discount_pct)}%` : null,
      Number(c.withholding_pct) ? `retém ${canon(c.withholding_pct)}%` : null].filter(Boolean).join(' · ') || 'As da empresa'],
    ['Conta corrente', balance === undefined ? '…' : owes > 0
      ? <span className={over ? 'font-semibold text-red-600' : 'font-medium text-amber-600'}>Deve {mt(balance)} MT{over ? ` · acima do limite (${mt(c.credit_limit!)} MT)` : ''}</span>
      : owes < 0 ? <span className="text-emerald-600">Crédito de {mt(-owes)} MT</span> : 'Sem valores em dívida'],
    ['Última compra', lastPurchase ? date(lastPurchase) : 'Primeira compra'],
  ];
  return (
    <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {facts.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-xs text-[var(--muted)]">{k}</dt>
          <dd className="truncate">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function DocumentEditPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { session, has } = useAuth();
  const ref = useReference();
  const existing = useQuery({ queryKey: ['document', id], enabled: !!id, queryFn: () => get(`/documents/${id}`) });

  const [type, setType] = useState<DocumentType>((sp.get('tipo') as DocumentType) || 'FT');
  const [customer, setCustomer] = useState<PickCustomer | null>(null);
  const [lines, setLines] = useState<Line[]>([emptyLine()]);
  const [o, setO] = useState({
    issueDate: '', dueDate: '', terms: '' as Terms, paymentMethod: '',
    notes: '', internalNotes: '', customerReference: '', reason: '', originDocumentId: '', deliveryAddress: '',
    globalDiscountPct: '', shipping: false, shippingAmount: '', shippingTaxRate: 16, withholdingPct: '', withholdingType: 'IRPC', movesStock: (sp.get('tipo') ?? 'FT') !== 'NC', currencyCode: 'MZN' as keyof typeof CURRENCIES, exchangeRate: '',
    loadPlace: '', unloadPlace: '', transportStart: '', vehiclePlate: '',
  });
  const [payment, setPayment] = useState({ method: 'NU', reference: '' });
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  // Adiantamentos (P-21): FR marcada como adiantamento; na FT, o valor a deduzir de cada adiantamento do cliente.
  const [isAdvance, setIsAdvance] = useState(false);
  const [adv, setAdv] = useState<Record<string, string>>({});
  const [newCustomer, setNewCustomer] = useState(false);
  const [catalog, setCatalog] = useState(false);
  const [busy, setBusy] = useState<'save' | 'issue' | null>(null);
  const [loaded, setLoaded] = useState(!id);
  const [more, setMore] = useState(false);
  const meta = DOCUMENT_TYPE_META[type];
  const hasPlannedPayment = ['FT', 'PF', 'ND'].includes(type);

  // Rascunho existente
  useEffect(() => {
    if (!existing.data || loaded) return;
    const { doc, lines: ls, customer: c, allocations, payments } = existing.data;
    if (doc.status !== 'draft') { nav(`/documentos/${id}`, { replace: true }); return; }
    setType(doc.doc_type); setCustomer(c);
    setLines(ls.length ? ls.map((l: any) => ({
      key: l.id, productId: l.product_id, productCode: l.product_code, description: l.description, note: l.note ?? '', quantity: canon(l.quantity), unit: l.unit,
      unitPrice: canon(l.unit_price_currency ?? l.unit_price), priceIncludesTax: l.price_includes_tax, discountPct: Number(l.discount_pct) ? canon(l.discount_pct) : '', taxRate: Number(l.tax_rate),
      exemptionCode: l.exemption_code ?? '', exemptionReason: l.exemption_code && !ref.data?.exemptions.find((x) => x.code === l.exemption_code)?.requires_text ? '' : (l.exemption_reason ?? ''),
    })) : [emptyLine()]);
    const ts = doc.transport_start ? new Date(doc.transport_start) : null;
    setO({
      issueDate: doc.issue_date ?? '', dueDate: doc.due_date ?? '',
      terms: doc.payment_terms_days !== null ? (String(doc.payment_terms_days) as Terms) : doc.due_date && doc.doc_type === 'FT' ? 'custom' : '',
      paymentMethod: doc.payment_method ?? '', notes: doc.notes ?? '', internalNotes: doc.internal_notes ?? '', customerReference: doc.customer_reference ?? '',
      reason: doc.reason ?? '', originDocumentId: doc.origin_document_id ?? '', deliveryAddress: doc.delivery_address ?? '',
      globalDiscountPct: Number(doc.global_discount_pct) ? canon(doc.global_discount_pct) : '', shipping: Number(doc.shipping_amount) > 0,
      shippingAmount: Number(doc.shipping_amount) ? canon(doc.shipping_amount_currency ?? doc.shipping_amount) : '', shippingTaxRate: Number(doc.shipping_tax_rate),
      currencyCode: doc.currency_code, exchangeRate: doc.currency_code === 'MZN' ? '' : canon(doc.exchange_rate),
      withholdingPct: Number(doc.withholding_pct) ? canon(doc.withholding_pct) : '', withholdingType: doc.withholding_type ?? 'IRPC', movesStock: doc.moves_stock,
      loadPlace: doc.load_place ?? '', unloadPlace: doc.unload_place ?? '', vehiclePlate: doc.vehicle_plate ?? '',
      transportStart: ts ? new Date(ts.getTime() - ts.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
    });
    setAlloc(Object.fromEntries(allocations.map((a: any) => [a.invoice_id, canon(a.amount)])));
    setIsAdvance(!!doc.is_advance);
    setAdv(Object.fromEntries((doc.advance_deductions ?? []).map((x: { documentId: string; amount: string }) => [x.documentId, canon(x.amount)])));
    if (payments[0]) setPayment({ method: payments[0].method, reference: payments[0].reference ?? '' });
    if (doc.internal_notes || doc.delivery_address || doc.notes) setMore(true);
    setLoaded(true);
  }, [existing.data, loaded, id, nav, ref.data]);

  // Forma de pagamento por omissão da empresa
  useEffect(() => {
    const def = session?.tenant.default_payment_method;
    if (!id && def) setO((x) => (x.paymentMethod ? x : { ...x, paymentMethod: def }));
  }, [session, id]);

  // Cliente indicado no URL, ou consumidor final por omissão em VD/FR
  const presetId = sp.get('cliente');
  const preset = useQuery({ queryKey: ['customer', presetId], enabled: !!presetId && !id, queryFn: () => get<{ customer: PickCustomer }>(`/customers/${presetId}`) });
  const wantsFinalConsumer = !id && !presetId && (type === 'VD' || type === 'FR');
  const finalConsumer = useQuery({ queryKey: ['final-consumer'], enabled: wantsFinalConsumer, queryFn: () => get<{ items: PickCustomer[] }>('/customers?pageSize=5&q=Consumidor') });
  useEffect(() => { if (preset.data && !customer) applyCustomer(preset.data.customer); }, [preset.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // Uma consulta desactivada continua a devolver o que está em cache (ex.: de uma VD aberta antes):
  // sem esta condição, o Consumidor Final ocupava o lugar do cliente indicado no endereço.
  useEffect(() => { if (!wantsFinalConsumer) return; const cf = finalConsumer.data?.items.find((c) => c.is_final_consumer); if (cf && !customer) applyCustomer(cf); }, [finalConsumer.data, wantsFinalConsumer]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Ao escolher o cliente herdam-se as condições comerciais da ficha. */
  function applyCustomer(c: PickCustomer | null) {
    setCustomer(c);
    if (!c || id) return;
    const days = c.payment_terms_days ?? session?.tenant.default_payment_terms_days ?? 30;
    setO((x) => ({
      ...x, withholdingPct: Number(c.withholding_pct) ? canon(c.withholding_pct) : '', withholdingType: c.withholding_type ?? 'IRPC',
      deliveryAddress: c.delivery_address ?? '', paymentMethod: c.default_payment_method ?? x.paymentMethod,
      terms: type === 'FT' ? (String(days) as Terms) : x.terms, dueDate: type === 'FT' ? '' : x.dueDate,
    }));
    if (c.default_payment_method) setPayment((p) => ({ ...p, method: c.default_payment_method! }));
    const d = Number(c.default_discount_pct) ? canon(c.default_discount_pct) : '';
    setLines((ls) => ls.map((l) => (l.description ? l : { ...l, discountPct: d })));
  }

  // Data: hoje por omissão (vazio = o dia da emissão); à mão, nunca antes do último documento da série.
  const rules = useQuery({ queryKey: ['date-rules', type], queryFn: () => get<{ today: string; minDate: string; lastIssueDate: string | null; series: string | null }>(`/documents/date-rules/${type}`) });
  const todayStr = rules.data?.today ?? today();
  const minDate = rules.data?.minDate;
  const dateError = o.issueDate && minDate && o.issueDate < minDate
    ? `Não pode ser anterior a ${date(minDate)}${rules.data?.lastIssueDate ? ' (último documento da série)' : ''}`
    : o.issueDate > todayStr ? 'Não pode ser futura' : undefined;
  // Ficha do cliente (contactos, conta corrente) para o cartão do editor.
  const custInfo = useQuery({
    queryKey: ['customer', customer?.id], enabled: !!customer && !customer.is_final_consumer,
    queryFn: () => get<{ customer: PickCustomer & { phone: string | null; credit_limit: string | null }; balance: string; stats: { invoices: number; last_purchase: string | null } }>(`/customers/${customer!.id}`),
  });

  const open = useQuery({ queryKey: ['open-invoices', customer?.id], enabled: type === 'RE' && !!customer, queryFn: () => get<{ items: any[] }>(`/documents/open-invoices/${customer!.id}`) });
  const filled = lines.filter((l) => l.description.trim());
  // Moeda estrangeira (P-01): os preços escrevem-se na moeda; como na API, convertem-se para MT ao câmbio, já arredondados.
  const foreign = o.currencyCode !== 'MZN' && type !== 'RE';
  const rate = foreign ? at(o.exchangeRate, SCALE.rate) : '1';
  const toMzn = (v: string, places: number) => (foreign ? toScale(new D(v).times(rate), places) : v);
  const totals = useMemo(() => {
    try {
      return calcDocument({
        lines: filled.map((l) => ({ quantity: at(l.quantity, SCALE.quantity), unitPrice: toMzn(at(l.unitPrice, SCALE.price), SCALE.price), discountPct: at(l.discountPct, SCALE.pct), taxRate: l.taxRate, priceIncludesTax: l.priceIncludesTax })),
        globalDiscountPct: at(o.globalDiscountPct, SCALE.pct), shippingAmount: o.shipping ? toMzn(at(o.shippingAmount, SCALE.money), SCALE.money) : '0', shippingTaxRate: o.shippingTaxRate, withholdingPct: at(o.withholdingPct, SCALE.pct),
      });
    } catch { return null; }
  }, [filled, o.globalDiscountPct, o.shipping, o.shippingAmount, o.shippingTaxRate, o.withholdingPct, foreign, rate]); // eslint-disable-line react-hooks/exhaustive-deps
  // Mercadoria (antes dos descontos de linha) e descontos de linha, em base tributável.
  const breakdown = useMemo(() => {
    if (!totals) return null;
    let gross = new D(0);
    filled.forEach((l, i) => {
      const g = new D(totals.lines[i]!.gross);
      gross = gross.plus(l.priceIncludesTax ? g.div(new D(1).plus(new D(l.taxRate).div(100))) : g);
    });
    const merch = money(gross);
    return { merch, lineDiscounts: money(new D(merch).minus(totals.linesNet)), units: filled.reduce((s, l) => s + Number(num(l.quantity)), 0) };
  }, [totals, filled]);
  const receiptTotal = Object.values(alloc).reduce((s, v) => s + Number(num(v)), 0);
  const advances = useQuery({ queryKey: ['advances', customer?.id], enabled: type === 'FT' && !!customer, queryFn: () => get<{ items: { id: string; full_number: string; issue_date: string; available: string }[] }>(`/documents/advances/${customer!.id}`) });
  const advTotal = Object.values(adv).reduce((s, v) => s.plus(at(v, SCALE.money)), new D(0));
  const setLine = (key: string, p: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  const defaultDiscount = customer && Number(customer.default_discount_pct) ? canon(customer.default_discount_pct) : '';
  const fromProduct = (p: PickProduct, keep?: Line): Partial<Line> => ({
    // Num documento noutra moeda, o preço da ficha (em MT) passa para a moeda ao câmbio escrito.
    productId: p.id, productCode: p.code, description: p.name, unit: p.unit, priceIncludesTax: p.price_includes_tax,
    unitPrice: foreign && Number(rate) > 0 ? canon(toScale(new D(p.unit_price).div(rate), SCALE.price)) : canon(p.unit_price),
    taxRate: Number(p.tax_rate), exemptionCode: p.exemption_code ?? '', note: keep?.note || (p.description ?? ''),
  });
  const addFromCatalog = (items: { product: PickProduct; quantity: string }[]) => setLines((ls) => [
    ...ls.filter((l) => l.description.trim()),
    ...items.map(({ product, quantity }) => ({ ...emptyLine(defaultDiscount), ...fromProduct(product), quantity: quantity || '1' })),
  ]);

  // Vencimento mostrado em função da condição de pagamento
  const effectiveDue = o.terms === 'custom' ? o.dueDate : o.terms !== '' ? addDays(o.issueDate || today(), Number(o.terms)) : '';
  const quoteDays = session?.tenant.quote_validity_days ?? 15;

  function body() {
    const payable = type === 'RE' ? receiptTotal.toFixed(2) : totals?.payableTotal ?? '0';
    return {
      docType: type, customerId: customer?.id, issueDate: o.issueDate || null,
      dueDate: o.terms === 'custom' || type === 'PF' ? o.dueDate || null : null,
      paymentTermsDays: type === 'FT' && o.terms !== '' && o.terms !== 'custom' ? Number(o.terms) : null,
      paymentMethod: hasPlannedPayment ? o.paymentMethod || null : null,
      originDocumentId: o.originDocumentId || null, reason: o.reason || null, notes: o.notes || null, internalNotes: o.internalNotes || null,
      customerReference: o.customerReference || null, deliveryAddress: o.deliveryAddress || null,
      globalDiscountPct: o.globalDiscountPct || '0', shippingAmount: o.shipping ? o.shippingAmount || '0' : '0', shippingTaxRate: o.shippingTaxRate,
      currencyCode: type === 'RE' ? 'MZN' : o.currencyCode, exchangeRate: foreign ? o.exchangeRate || '0' : '1',
      withholdingPct: o.withholdingPct || '0', withholdingType: o.withholdingPct ? o.withholdingType : null,
      movesStock: o.movesStock, loadPlace: o.loadPlace || null, unloadPlace: o.unloadPlace || null, vehiclePlate: o.vehiclePlate || null, transportStart: o.transportStart || null,
      lines: type === 'RE' ? [] : filled.map((l) => ({
        productId: l.productId, productCode: l.productCode, description: l.description, note: l.note || null, quantity: l.quantity, unit: l.unit,
        unitPrice: l.unitPrice || '0', priceIncludesTax: l.priceIncludesTax, discountPct: l.discountPct || '0', taxRate: l.taxRate,
        exemptionCode: l.taxRate === 0 ? l.exemptionCode || null : null, exemptionReason: l.taxRate === 0 ? l.exemptionReason || null : null,
      })),
      allocations: type === 'RE' ? Object.entries(alloc).filter(([, v]) => Number(num(v)) > 0).map(([invoiceId, amount]) => ({ invoiceId, amount })) : [],
      payments: meta.paidOnIssue ? [{ method: payment.method, amount: payable, reference: payment.reference || null }] : [],
      isAdvance: type === 'FR' && isAdvance,
      advances: type === 'FT' ? Object.entries(adv).filter(([, v]) => Number(num(v)) > 0).map(([documentId, amount]) => ({ documentId, amount })) : [],
    };
  }

  async function save(issue: boolean, overrides: { overrideCreditLimit?: boolean; overrideDiscount?: boolean } = {}): Promise<void> {
    if (!customer) { toast('Escolha o cliente', 'error'); return; }
    if (dateError) { toast(`Data do documento: ${dateError.toLowerCase()}`, 'error'); return; }
    const isOverride = !!(overrides.overrideCreditLimit || overrides.overrideDiscount);
    if (issue && !isOverride) {
      if (['FT', 'PF'].includes(type) && !o.paymentMethod) { toast('Indique a forma de pagamento', 'error'); return; }
      const ok = await confirm({
        title: `Emitir ${meta.label.toLowerCase()}?`,
        description: meta.fiscal
          ? <>O sistema atribui o número, assina o documento e regista-o. <b>Depois de emitido não pode ser alterado nem apagado</b> — só corrigido por nota de crédito/débito.</>
          : 'O documento fica numerado e deixa de ser editável.',
        confirmLabel: `Emitir · ${mt(type === 'RE' ? receiptTotal : totals?.grossTotal ?? 0)} MT`,
      });
      if (!ok) return;
    }
    setBusy(issue ? 'issue' : 'save');
    let docId = id;
    try {
      if (!isOverride) {
        if (docId) await patch(`/documents/${docId}`, body());
        else docId = (await post<{ id: string }>('/documents', body())).id;
      }
      if (issue) {
        const r = await post<{ number: string }>(`/documents/${docId}/issue`, overrides);
        toast(`${meta.label} ${r.number} emitida`);
      } else toast('Rascunho guardado');
      ['documents', 'dashboard', 'nav-counts'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      nav(`/documentos/${docId}`, { replace: true });
    } catch (e) {
      if (docId && !id) nav(`/documentos/${docId}/editar`, { replace: true });
      if (e instanceof ApiError && (e.code === 'credit_limit' || e.code === 'discount_ceiling') && (e.body.details as { overridable?: boolean })?.overridable) {
        setBusy(null);
        const ok = await confirm({ title: e.code === 'credit_limit' ? 'Limite de crédito ultrapassado' : 'Desconto acima do permitido', description: <>{e.message}<br /><br />Pode emitir mesmo assim; a autorização fica registada na auditoria em seu nome.</>, tone: 'danger', confirmLabel: 'Autorizar e emitir' });
        if (ok) return save(true, e.code === 'credit_limit' ? { overrideCreditLimit: true } : { overrideDiscount: true });
        return;
      }
      toast(errorText(e), 'error');
    } finally { setBusy(null); }
  }

  if (!loaded || ref.isLoading) return <Loading />;
  const exemptions = ref.data?.exemptions ?? [];
  const noNuit = customer && !customer.nuit && !customer.is_final_consumer && ['FT', 'NC', 'ND'].includes(type);
  const actions = (
    <>
      <Button variant="secondary" loading={busy === 'save'} disabled={!!busy} onClick={() => save(false)}>Guardar rascunho</Button>
      <Button loading={busy === 'issue'} disabled={!!busy} icon={<Send className="size-4" />} onClick={() => save(true)}>Emitir</Button>
    </>
  );

  return (
    <>
      <PageHeader eyebrow={<Link to={`/vendas?tipo=${type}`} className="hover:underline">Vendas</Link>}
        title={`${id ? 'Editar' : 'Nova'} ${meta.label.toLowerCase()}`}
        subtitle={meta.fiscal ? 'O número é atribuído pelo sistema no momento da emissão.' : 'Documento sem valor fiscal.'}
        actions={<>
          <div className="mr-2 text-right leading-tight">
            <div className="text-xs text-[var(--muted)]">{type === 'RE' ? 'Total recebido' : 'Total'}</div>
            <div className="text-xl font-bold tabular-nums">{mt(type === 'RE' ? receiptTotal : totals?.grossTotal ?? 0)} <span className="text-xs font-medium text-[var(--muted)]">MT</span></div>
          </div>
          {actions}
        </>} />

      <div className="space-y-6">
        {/* ── Cliente e dados do documento ─────────────────────────────────── */}
        <div className="grid gap-6 lg:grid-cols-3">
          <Card title="Cliente" className="lg:col-span-2" actions={!o.originDocumentId && <Button size="sm" variant="soft" icon={<UserPlus className="size-4" />} onClick={() => setNewCustomer(true)}>Novo cliente</Button>}>
            <CustomerPicker value={customer} onChange={applyCustomer} autoFocus={!id && !presetId} disabled={!!o.originDocumentId} />
            {customer && <CustomerFacts c={custInfo.data?.customer ?? customer} balance={custInfo.data?.balance} lastPurchase={custInfo.data?.stats.last_purchase} />}
            {!customer && <p className="mt-3 text-sm text-[var(--muted)]">Pesquise pelo nome, NUIT ou código. A morada, o prazo, a forma de pagamento, o desconto e a retenção vêm da ficha do cliente.</p>}
            {noNuit && <div className="mt-3"><Alert tone="warn">Este cliente não tem NUIT — obrigatório para emitir {meta.label.toLowerCase()} a empresas (CIVA art. 27 n.º 5 a). <Link className="font-semibold underline" to={`/clientes/${customer!.id}`}>Completar ficha</Link></Alert></div>}
          </Card>

          <Card title="Documento">
            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Data" error={dateError} hint={o.issueDate ? (minDate ? `Desde ${date(minDate)}` : undefined) : 'Hoje · automática'}>
                  <Input type="date" min={minDate} max={todayStr} value={o.issueDate || todayStr}
                    onChange={(e) => setO({ ...o, issueDate: e.target.value === todayStr ? '' : e.target.value })} />
                </Field>
                <Field label="Requisição"><Input value={o.customerReference} onChange={(e) => setO({ ...o, customerReference: e.target.value })} placeholder="N.º do cliente" /></Field>
              </div>
              {type !== 'RE' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Moeda" hint={foreign ? 'Preços nesta moeda; os totais fiscais saem em MT' : undefined}>
                    <Select value={o.currencyCode} disabled={['NC', 'ND', 'GD'].includes(type) && !!o.originDocumentId}
                      onChange={(e) => setO({ ...o, currencyCode: e.target.value as keyof typeof CURRENCIES })}>
                      {Object.entries(CURRENCIES).map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
                    </Select>
                  </Field>
                  {foreign && (
                    <Field label={`Câmbio (MT por 1 ${o.currencyCode})`} required>
                      <Input className="num" inputMode="decimal" value={o.exchangeRate} disabled={['NC', 'ND', 'GD'].includes(type) && !!o.originDocumentId}
                        onChange={(e) => setO({ ...o, exchangeRate: e.target.value })} placeholder="Ex.: 63,90" />
                    </Field>
                  )}
                </div>
              )}
              {type === 'FT' && (
                <div className="grid grid-cols-[3fr_2fr] gap-3">
                  <Field label="Condição">
                    <Select value={o.terms} onChange={(e) => setO({ ...o, terms: e.target.value as Terms })}>
                      <option value="">Prazo do cliente</option>
                      {/* Os prazos habituais e, se for outro, o da ficha do cliente ou do rascunho (ex.: 25 dias). */}
                      {[...new Set<number>([...PAYMENT_TERMS, ...(o.terms !== '' && o.terms !== 'custom' ? [Number(o.terms)] : [])])].sort((x, y) => x - y)
                        .map((d) => <option key={d} value={d}>{d === 0 ? 'Pronto pagamento' : `${d} dias`}</option>)}
                      <option value="custom">Data acordada…</option>
                    </Select>
                  </Field>
                  <Field label="Vencimento">
                    {o.terms === 'custom' ? <Input type="date" value={o.dueDate} onChange={(e) => setO({ ...o, dueDate: e.target.value })} />
                      : <div className="flex h-9.5 items-center rounded-lg bg-[var(--surface-2)] px-3 text-sm tabular-nums">{effectiveDue ? date(effectiveDue) : 'Ao emitir'}</div>}
                  </Field>
                </div>
              )}
              {type === 'PF' && <Field label="Válida até" hint={`Vazio = ${quoteDays} dias`}><Input type="date" value={o.dueDate} onChange={(e) => setO({ ...o, dueDate: e.target.value })} /></Field>}
              {hasPlannedPayment && (
                <Field label="Forma de pagamento" required={type !== 'ND'}>
                  <Select value={o.paymentMethod} onChange={(e) => setO({ ...o, paymentMethod: e.target.value })}>
                    <option value="">— Escolha —</option>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Select>
                </Field>
              )}
              {meta.paidOnIssue && type !== 'RE' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Pago com"><Select value={payment.method} onChange={(e) => setPayment({ ...payment, method: e.target.value })}>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
                  <Field label="Referência"><Input value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} placeholder={payment.method === 'NU' ? '—' : 'ID da transacção'} disabled={payment.method === 'NU'} /></Field>
                </div>
              )}
              {type === 'RE' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Recebido com"><Select value={payment.method} onChange={(e) => setPayment({ ...payment, method: e.target.value })}>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
                  <Field label="Referência"><Input value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} disabled={payment.method === 'NU'} /></Field>
                </div>
              )}
            </div>
          </Card>
        </div>

        {meta.requiresOrigin && !o.originDocumentId && (
          <Alert tone="warn" title={`${meta.label} sem documento de origem`}>
            Este documento tem de referir o documento que corrige. Abra a factura (ou factura-recibo / venda a dinheiro) em <Link className="font-semibold underline" to="/vendas">Vendas</Link> e use o menu ⋯ → <b>Emitir nota de crédito</b> / <b>de débito</b>{type === 'GD' ? <> / <b>Criar guia de devolução</b></> : null}.
          </Alert>
        )}
        {meta.requiresOrigin && (
          <Card title="Correcção" subtitle="Referência ao documento original">
            <Field label="Motivo" required hint="Impresso no documento. Ex.: devolução de mercadoria, erro no preço, desconto concedido depois da factura.">
              <Textarea rows={2} value={o.reason} onChange={(e) => setO({ ...o, reason: e.target.value })} />
            </Field>
          </Card>
        )}

        {/* ── Linhas / documentos a liquidar ───────────────────────────────── */}
        {type === 'RE' ? (
          <Card title="Documentos a liquidar" padded={false}>
            {!customer ? <p className="p-5 text-sm text-[var(--muted)]">Escolha o cliente para ver as facturas em aberto.</p> : open.isLoading ? <div className="p-5"><Loading rows={3} /></div> : !open.data?.items.length ? (
              <p className="p-5 text-sm text-[var(--muted)]">Este cliente não tem facturas em aberto.</p>
            ) : (
              <Table>
                <thead><tr><Th>Documento</Th><Th>Vencimento</Th><Th className="text-right">Pendente</Th><Th className="w-52 text-right">A liquidar</Th></tr></thead>
                <tbody>{open.data.items.map((f) => (
                  <tr key={f.id}>
                    <Td className="font-medium">{f.full_number}</Td>
                    <Td className={f.days_overdue > 0 ? 'text-red-600' : ''}>{date(f.due_date)} {f.days_overdue > 0 && <Badge tone="red">{f.days_overdue} dias</Badge>}</Td>
                    <Td className="num">{mt(f.pending)}</Td>
                    <Td><div className="flex justify-end gap-1">
                      <Input className="num h-8 w-28" inputMode="decimal" aria-label={`Liquidar ${f.full_number}`} value={alloc[f.id] ?? ''} onChange={(e) => setAlloc({ ...alloc, [f.id]: e.target.value })} />
                      <Button size="sm" variant="soft" onClick={() => setAlloc({ ...alloc, [f.id]: canon(f.pending) })}>Tudo</Button>
                    </div></Td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
          </Card>
        ) : (
          <Card title="Linhas" subtitle={breakdown && filled.length ? `${filled.length} linha${filled.length === 1 ? '' : 's'} · ${breakdown.units} unidades` : 'Escreva para procurar no catálogo ou use texto livre'} padded={false}
            actions={<>
              {defaultDiscount && <Badge tone="teal">Desconto habitual {defaultDiscount}%</Badge>}
              <Button size="sm" variant="secondary" icon={<BookOpen className="size-4" />} onClick={() => setCatalog(true)}>Catálogo</Button>
            </>}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-sm">
                <thead><tr className="bg-[var(--surface-2)] text-left text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase">
                  <th className="w-10 py-2.5 pl-5">#</th><th className="py-2.5">Artigo / descrição</th><th className="w-24 px-2 text-right">Qtd.</th><th className="w-20 px-2">Un.</th>
                  <th className="w-32 px-2 text-right">Preço unit.{foreign ? ` (${o.currencyCode})` : ''}</th><th className="w-24 px-2 text-right">Desc. %</th><th className="w-28 px-2">IVA</th>
                  <th className="w-32 px-2 text-right">Valor s/ IVA</th><th className="w-20 pr-5" />
                </tr></thead>
                <tbody>
                  {lines.map((l, i) => {
                    const res = totals?.lines[filled.indexOf(l)];
                    const needsReason = l.taxRate === 0 && !l.exemptionCode && l.description;
                    return (
                      <tr key={l.key} className="border-t border-[var(--border)] align-top">
                        <td className="py-4 pl-5 text-xs text-[var(--muted)] tabular-nums">{i + 1}</td>
                        <td className="py-2 pr-2">
                          <ProductSuggest value={l.description} ariaLabel={`Descrição da linha ${i + 1}`} onBrowse={() => setCatalog(true)}
                            onText={(v) => setLine(l.key, { description: v, productId: null, productCode: null })}
                            onPick={(p) => setLine(l.key, fromProduct(p, l))} />
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                            {l.productCode && <Badge>{l.productCode}</Badge>}
                            {l.priceIncludesTax && <Badge tone="blue">preço c/ IVA</Badge>}
                            {l.note && !l.open && <span className="truncate text-[var(--muted)]">{l.note}</span>}
                            {needsReason && <Badge tone="red">falta motivo de isenção</Badge>}
                          </div>
                          {l.open && (
                            <div className="mt-2 grid gap-3 rounded-xl bg-[var(--surface-2)] p-3">
                              <Field label="Nota impressa na linha"><Input className="h-8" value={l.note} onChange={(e) => setLine(l.key, { note: e.target.value })} /></Field>
                              <Switch label="O preço inclui IVA" hint="Retalho: o cliente paga exactamente este valor (CIVA art. 30)" checked={l.priceIncludesTax} onChange={(v) => setLine(l.key, { priceIncludesTax: v })} />
                            </div>
                          )}
                          {l.taxRate === 0 && (
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                              <Select className="h-8 text-xs" aria-label="Motivo de isenção" value={l.exemptionCode} onChange={(e) => setLine(l.key, { exemptionCode: e.target.value })}>
                                <option value="">Motivo de isenção (obrigatório)…</option>
                                {exemptions.map((x) => <option key={x.code} value={x.code}>{x.code} — {x.description}</option>)}
                                {ref.data?.expiredExemptions?.filter((x) => x.code === l.exemptionCode).map((x) => <option key={x.code} value={x.code} disabled>{x.code} — caducou em {x.valid_to?.split('-').reverse().join('/')}: escolha outro</option>)}
                              </Select>
                              {exemptions.find((x) => x.code === l.exemptionCode)?.requires_text && <Input className="h-8 text-xs" placeholder="Base legal" value={l.exemptionReason} onChange={(e) => setLine(l.key, { exemptionReason: e.target.value })} />}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2"><Input className="num" inputMode="decimal" aria-label={`Quantidade da linha ${i + 1}`} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} /></td>
                        <td className="px-2 py-2"><SuggestInput suggestions={UNITS} maxLength={15} aria-label={`Unidade da linha ${i + 1}`} value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value })} /></td>
                        <td className="px-2 py-2"><Input className="num" inputMode="decimal" aria-label={`Preço da linha ${i + 1}`} value={l.unitPrice} onChange={(e) => setLine(l.key, { unitPrice: e.target.value })} /></td>
                        <td className="px-2 py-2"><Input className="num" inputMode="decimal" aria-label={`Desconto da linha ${i + 1}`} value={l.discountPct} onChange={(e) => setLine(l.key, { discountPct: e.target.value })} /></td>
                        <td className="px-2 py-2"><Select aria-label={`IVA da linha ${i + 1}`} value={l.taxRate} onChange={(e) => setLine(l.key, { taxRate: Number(e.target.value) })}><option value={16}>16%</option><option value={5}>5%</option><option value={0}>Isento</option></Select></td>
                        <td className="px-2 py-2 text-right">
                          <div className="pt-2 font-semibold tabular-nums">{res ? mt(res.netAmount) : '—'}</div>
                          {res && Number(res.taxAmount) > 0 && <div className="text-[11px] text-[var(--muted)] tabular-nums">IVA {mt(res.taxAmount)}</div>}
                        </td>
                        <td className="py-2 pr-5"><div className="flex justify-end">
                          <Button variant="ghost" size="sm" iconOnly icon={<SlidersHorizontal className="size-4" />} aria-label="Mais opções da linha" className={clsx(l.open && 'bg-[var(--surface-2)]')} onClick={() => setLine(l.key, { open: !l.open })} />
                          <Button variant="ghost" size="sm" iconOnly icon={<Trash2 className="size-4" />} aria-label={`Remover linha ${i + 1}`} onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [emptyLine(defaultDiscount)]))} />
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-[var(--border)] px-5 py-3">
              <Button variant="soft" size="sm" icon={<Plus className="size-4" />} onClick={() => setLines([...lines, emptyLine(defaultDiscount)])}>Adicionar linha</Button>
              <Button variant="ghost" size="sm" icon={<BookOpen className="size-4" />} onClick={() => setCatalog(true)}>Adicionar do catálogo</Button>
            </div>
          </Card>
        )}

        {type === 'FT' && !!advances.data?.items.length && (
          <Card title="Adiantamentos a deduzir" subtitle="Recebidos deste cliente e ainda por deduzir. Ao emitir, sai uma nota de crédito sobre cada adiantamento (regulariza o IVA) e o cliente fica a dever só a diferença." padded={false}>
            <Table>
              <thead><tr><Th>Adiantamento</Th><Th>Data</Th><Th className="text-right">Disponível</Th><Th className="w-52 text-right">A deduzir</Th></tr></thead>
              <tbody>{advances.data.items.map((x) => (
                <tr key={x.id}>
                  <Td className="font-medium">{x.full_number}</Td><Td>{date(x.issue_date)}</Td><Td className="num">{mt(x.available)}</Td>
                  <Td><div className="flex justify-end gap-1">
                    <Input className="num h-8 w-28" inputMode="decimal" aria-label={`Deduzir ${x.full_number}`} value={adv[x.id] ?? ''} onChange={(e) => setAdv({ ...adv, [x.id]: e.target.value })} />
                    <Button size="sm" variant="soft" onClick={() => setAdv({ ...adv, [x.id]: canon(D.min(x.available, totals?.payableTotal ?? x.available).toString()) })}>Tudo</Button>
                  </div></Td>
                </tr>
              ))}</tbody>
            </Table>
          </Card>
        )}

        {(type === 'GR' || type === 'GD') && (
          <Card title={<span className="flex items-center gap-2"><Truck className="size-4" />Transporte</span>} subtitle="Obrigatório nas guias: local de carga, de descarga e início do transporte">
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Local de carga" required><Input value={o.loadPlace} onChange={(e) => setO({ ...o, loadPlace: e.target.value })} placeholder="Armazém, morada" /></Field>
              <Field label="Local de descarga" required><Input value={o.unloadPlace} onChange={(e) => setO({ ...o, unloadPlace: e.target.value })} placeholder="Morada de entrega" /></Field>
              <Field label="Início do transporte" required><Input type="datetime-local" value={o.transportStart} onChange={(e) => setO({ ...o, transportStart: e.target.value })} /></Field>
              <Field label="Matrícula"><Input value={o.vehiclePlate} onChange={(e) => setO({ ...o, vehiclePlate: e.target.value.toUpperCase() })} placeholder="AAA-123-MC" /></Field>
            </div>
          </Card>
        )}

        {/* ── Ajustes e resumo ─────────────────────────────────────────────── */}
        <div className="grid items-start gap-6 lg:grid-cols-[1fr_400px]">
          <div className="space-y-6">
            {type !== 'RE' && (
              <Card title="Descontos, encargos e retenção">
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Desconto global (%)" hint="Sobre o total das linhas"><Input inputMode="decimal" value={o.globalDiscountPct} onChange={(e) => setO({ ...o, globalDiscountPct: e.target.value })} /></Field>
                    <Field label="Retenção na fonte (%)" hint="Ex.: 10% em serviços"><Input inputMode="decimal" value={o.withholdingPct} onChange={(e) => setO({ ...o, withholdingPct: e.target.value })} /></Field>
                    <Field label="Imposto retido"><Select value={o.withholdingType} onChange={(e) => setO({ ...o, withholdingType: e.target.value })}><option>IRPC</option><option>IRPS</option></Select></Field>
                  </div>
                  <div className="rounded-xl border border-[var(--border)] p-4">
                    <Switch label="Transporte / portes" hint="Encargo de transporte facturado ao cliente, com IVA próprio" checked={o.shipping} onChange={(v) => setO({ ...o, shipping: v })} />
                    {o.shipping && (
                      <div className="mt-3 grid gap-4 sm:grid-cols-2">
                        <Field label="Valor (MT, sem IVA)"><Input inputMode="decimal" value={o.shippingAmount} onChange={(e) => setO({ ...o, shippingAmount: e.target.value })} /></Field>
                        <Field label="IVA do transporte"><Select value={o.shippingTaxRate} onChange={(e) => setO({ ...o, shippingTaxRate: Number(e.target.value) })}><option value={16}>16%</option><option value={5}>5%</option><option value={0}>Isento</option></Select></Field>
                      </div>
                    )}
                  </div>
                  {type === 'FR' && <Switch label="Factura-recibo de adiantamento" hint="Pagamento antecipado com IVA, a deduzir numa factura posterior. Sem desconto global, portes nem retenção." checked={isAdvance} onChange={(v) => { setIsAdvance(v); if (v) setO({ ...o, movesStock: false }); }} />}
                  {meta.stock && has('stock') && !(type === 'FR' && isAdvance) && <Switch label={meta.stock === 'in' ? 'Repor stock (devolução)' : 'Dar saída do stock'} hint="Só para artigos com controlo de stock" checked={o.movesStock} onChange={(v) => setO({ ...o, movesStock: v })} />}
                </div>
              </Card>
            )}
            <Card padded={false}>
              <button type="button" aria-expanded={more} onClick={() => setMore(!more)} className="flex w-full items-center justify-between px-5 py-3.5 text-left text-[15px] font-semibold">
                Observações e entrega <ChevronDown className={clsx('size-4 transition', more && 'rotate-180')} />
              </button>
              {more && (
                <div className="grid gap-4 border-t border-[var(--border)] p-5 sm:grid-cols-2">
                  <Field label="Observações (impressas)" className="sm:col-span-2"><Textarea rows={2} value={o.notes} onChange={(e) => setO({ ...o, notes: e.target.value })} /></Field>
                  <Field label="Morada de entrega"><Input value={o.deliveryAddress} onChange={(e) => setO({ ...o, deliveryAddress: e.target.value })} /></Field>
                  <Field label="Notas internas" hint="Nunca impressas nem enviadas"><Input value={o.internalNotes} onChange={(e) => setO({ ...o, internalNotes: e.target.value })} /></Field>
                </div>
              )}
            </Card>
          </div>

          <Card title="Resumo">
            {type === 'RE' ? (
              <div className="flex items-baseline justify-between"><span className="text-[var(--muted)]">Total recebido</span><span className="text-2xl font-bold tabular-nums">{mt(receiptTotal)} MT</span></div>
            ) : totals && breakdown ? (
              <>
                <dl className="text-sm">
                  <Row label="Mercadoria / serviços" value={breakdown.merch} />
                  <Row label="Descontos comerciais" value={breakdown.lineDiscounts} neg />
                  <Row label={`Desconto global${Number(num(o.globalDiscountPct)) ? ` (${o.globalDiscountPct}%)` : ''}`} value={totals.globalDiscountAmount} neg />
                  <Row label="Transporte / portes" value={totals.shippingAmount} />
                  <div className="my-1.5 border-t border-dashed border-[var(--border)]" />
                  <Row label="Base tributável" value={totals.netTotal} strong />
                  {totals.taxSummary.map((t) => <Row key={t.rate} muted label={Number(t.rate) ? `IVA ${Number(t.rate)}% sobre ${mt(t.netAmount)}` : `Isento sobre ${mt(t.netAmount)}`} value={t.taxAmount} />)}
                  <Row label="Total de IVA" value={totals.taxTotal} />
                </dl>
                <div className="mt-3 flex items-baseline justify-between rounded-xl bg-brand-500/10 px-4 py-3">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold tabular-nums">{mt(totals.grossTotal)} <span className="text-sm font-medium text-[var(--muted)]">MT</span></span>
                </div>
                {foreign && Number(rate) > 0 && (
                  <div className="mt-2 flex justify-between text-sm text-[var(--muted)]"><span>Contravalor em {o.currencyCode} (câmbio {o.exchangeRate})</span><span className="tabular-nums">{mt(new D(totals.payableTotal).div(rate).toFixed(2))} {o.currencyCode}</span></div>
                )}
                {type === 'FT' && advTotal.greaterThan(0) && (
                  <dl className="mt-2 text-sm">
                    <Row label="Adiantamentos deduzidos" value={money(advTotal)} neg />
                    <Row label="Valor em dívida" value={money(new D(totals.payableTotal).minus(advTotal))} strong />
                  </dl>
                )}
                {Number(totals.withholdingAmount) > 0 && (
                  <dl className="mt-2 text-sm">
                    <Row label={`Retenção na fonte ${o.withholdingType} (${o.withholdingPct}%)`} value={totals.withholdingAmount} neg />
                    <Row label="Total a receber" value={totals.payableTotal} strong />
                  </dl>
                )}
                <dl className="mt-3 space-y-1 border-t border-[var(--border)] pt-3 text-xs text-[var(--muted)]">
                  {type === 'FT' && <div className="flex justify-between"><dt>Vencimento</dt><dd>{effectiveDue ? date(effectiveDue) : 'prazo do cliente'}</dd></div>}
                  {hasPlannedPayment && <div className="flex justify-between"><dt>Forma de pagamento</dt><dd>{o.paymentMethod ? PAYMENT_METHODS[o.paymentMethod as keyof typeof PAYMENT_METHODS] : '—'}</dd></div>}
                  {meta.paidOnIssue && <div className="flex justify-between"><dt>Pago com</dt><dd>{PAYMENT_METHODS[payment.method as keyof typeof PAYMENT_METHODS]}</dd></div>}
                  <div className="flex justify-between"><dt>Moeda</dt><dd>{foreign ? `${o.currencyCode} · totais fiscais em MT` : 'Metical (MZN)'}</dd></div>
                  {session?.tenant.training_mode && <div className="pt-1 text-amber-700 dark:text-amber-400">Modo formação: marca de água e série de formação.</div>}
                </dl>
              </>
            ) : <Alert tone="error">Há valores inválidos nas linhas.</Alert>}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">{actions}</div>
          </Card>
        </div>
      </div>

      <Modal open={newCustomer} onClose={() => setNewCustomer(false)} title="Novo cliente" size="lg" icon={<UserPlus />}>
        <CustomerForm onCancel={() => setNewCustomer(false)} onSaved={(c) => { applyCustomer(c as PickCustomer); setNewCustomer(false); qc.invalidateQueries({ queryKey: ['picker'] }); }} />
      </Modal>
      <CatalogModal open={catalog} onClose={() => setCatalog(false)} onAdd={addFromCatalog} />
    </>
  );
}
